import React, { useEffect, useState, useCallback } from "react";
import axiosInstance from "../../../interceptors";
import { baseUrl } from "../../../config/config";
import { toast } from "../../../components/shared/toast";

const STATUS_COLORS = {
  NEW:         "bg-amber-100 text-amber-700",
  REVIEWED:    "bg-blue-100 text-blue-700",
  SHORTLISTED: "bg-emerald-100 text-emerald-700",
  REJECTED:    "bg-red-100 text-red-700",
  CONVERTED:   "bg-violet-100 text-violet-700",
};

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

export default function Applications() {
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      axiosInstance.get(`${baseUrl}/job-posting/applications`),
      axiosInstance.get(`${baseUrl}/job-posting/applications/stats`),
    ]).then(([r1, r2]) => {
      setApps(Array.isArray(r1.data) ? r1.data : []);
      setStats(r2.data || {});
    }).catch(() => { setApps([]); setStats({}); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    document.title = "Applications - Ample Hire";
    load();
  }, [load]);

  const review = async (app, status) => {
    await axiosInstance.put(`${baseUrl}/job-posting/applications/${app.id}/review`, { status });

    // Fire the stage-transition email after the status flip. Best-effort —
    // the status change is the source of truth and shouldn't roll back if
    // email send hiccups. Server auto-resolves feedbackUrl when the
    // candidate has a scored interview (#2).
    if ((status === "SHORTLISTED" || status === "REJECTED") && app.email) {
      const candidateName = [app.firstName, app.lastName].filter(Boolean).join(" ") || app.email;
      try {
        await axiosInstance.post(`${baseUrl}/job-posting/stage-emails/send`, {
          stageTo: status,
          candidateId: app.candidateId || app.userId || null,
          jobId: app.jobId ? Number(app.jobId) : null,
          recipientEmail: app.email,
          variables: {
            candidateName,
            jobTitle: app.jobId ? `Job #${app.jobId}` : "the role",
            companyName: "Ample Hire",
            recruiterName: "the hiring team",
            ...(status === "SHORTLISTED" && {
              nextStepDescription: "We'll be in touch shortly with next steps.",
            }),
          },
        });
        toast.success(status === "SHORTLISTED" ? "Shortlisted — email sent" : "Rejected — email sent");
      } catch (e) {
        // Status flip succeeded; only the email leg failed. Tell the user
        // so they can resend manually rather than thinking nothing happened.
        toast.error("Status updated but email failed", {
          description: e?.response?.data?.message || e?.message || "Try sending from the candidate report.",
        });
      }
    }
    load();
  };

  const filtered = filter ? apps.filter((a) => a.status === filter) : apps;

  return (
    <div className="page-enter">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-vf-base">Applications</h1>
        <p className="text-sm text-vf-muted mt-1">Inbound applications from the public careers page.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        {["total", "new", "reviewed", "shortlisted", "rejected"].map((k) => (
          <button
            key={k}
            onClick={() => setFilter(k === "total" ? "" : k.toUpperCase())}
            className={`bg-white border rounded-xl p-3 text-left ${
              (filter === "" && k === "total") || filter === k.toUpperCase()
                ? "border-vf-primary"
                : "border-slate-200"
            }`}
          >
            <div className="text-xs text-vf-muted uppercase">{k}</div>
            <div className="text-2xl font-semibold text-vf-base">{stats[k] ?? 0}</div>
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-vf-muted">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Name</th>
              <th className="text-left px-4 py-2 font-medium">Email</th>
              <th className="text-left px-4 py-2 font-medium">Job</th>
              <th className="text-left px-4 py-2 font-medium">Source</th>
              <th className="text-left px-4 py-2 font-medium">Status</th>
              <th className="text-left px-4 py-2 font-medium">Applied</th>
              <th className="text-right px-4 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={7} className="px-4 py-6 text-center text-vf-muted text-xs">Loading…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-vf-muted text-xs">No applications.</td></tr>
            )}
            {filtered.map((a) => (
              <tr key={a.id}>
                <td className="px-4 py-2 text-vf-base">{a.firstName} {a.lastName}</td>
                <td className="px-4 py-2 text-vf-muted text-xs">{a.email}</td>
                <td className="px-4 py-2 text-vf-base">#{a.jobId}</td>
                <td className="px-4 py-2 text-vf-muted text-xs">{a.source}</td>
                <td className="px-4 py-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[a.status] || "bg-slate-100"}`}>
                    {a.status}
                  </span>
                </td>
                <td className="px-4 py-2 text-vf-muted text-xs">{fmt(a.createdAt)}</td>
                <td className="px-4 py-2 text-right space-x-1">
                  {a.status === "NEW" && (
                    <button onClick={() => review(a, "REVIEWED")} className="px-2 py-1 rounded border border-slate-300 text-xs">Review</button>
                  )}
                  {a.status !== "SHORTLISTED" && a.status !== "REJECTED" && (
                    <>
                      <button onClick={() => review(a, "SHORTLISTED")} className="px-2 py-1 rounded bg-emerald-600 text-white text-xs">Shortlist</button>
                      <button onClick={() => review(a, "REJECTED")} className="px-2 py-1 rounded bg-red-600 text-white text-xs">Reject</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
